import {
  type CharacterShape,
  type Stroke,
  type StrokePoint,
  type StrokePoints,
  placeSteps,
} from "./stroke-shape.js";
import { strokeCost } from "./stroke-distance.js";

/**
 * What the ink of one stroke costs against each stroke of a character, and
 * the two moves `stroke-step.ts` makes with it before reading the answer.
 *
 * The cost is the lookup's `strokeCost`. What this adds is the bound on where
 * the ink sits, and the arithmetic of moving ink onto a stroke to ask whether
 * its shape was right whatever its place was.
 */

/**
 * How far the middle of the ink may sit from the middle of a stroke it is
 * taken for, in cells of the place grid.
 *
 * The cost alone would take a 竖 of the right length drawn three cells from
 * where it belongs, since a cell of place costs 0.114 and nothing else about
 * the stroke differs. A drill is teaching where the strokes go. A cell and a
 * half is a fifth of the square, which the messiest hand measured stays well
 * inside, and ink further off than that with the right shape is read as the
 * right stroke in the wrong place.
 */
export const placeCells = 1.5;

/** How far apart two strokes' middles are, in cells of the place grid. */
function apart(one: Stroke, other: Stroke): number {
  return Math.hypot(one.column - other.column, one.row - other.row);
}

/**
 * What the ink cost against each stroke of the character, in writing order.
 *
 * A stroke further off than {@link placeCells} costs more than any stroke
 * taken may, so it drops out of the running for right and for order and is
 * left to the place reading.
 *
 * @param drawn - The ink, described in the square.
 * @param stored - The character's strokes, described in the square.
 * @returns A cost per stroke of the character.
 */
export function costsAgainst(
  drawn: Stroke,
  stored: CharacterShape,
  coarse: CharacterShape,
): number[] {
  return stored.map((stroke, at) =>
    apart(drawn, stroke) > placeCells
      ? Infinity
      : leastCost(drawn, stroke, coarse[at]),
  );
}

/**
 * The lower of the ink's costs against two descriptions of one stroke.
 *
 * The outlines write the 竖 of 十 with a lead-in at the top, and a reader
 * draws it straight. Described at the measured corner tolerance the lead-in
 * is a run of its own and the straight ink costs 0.44 against it. Described
 * coarsely it is one run and the ink costs a tenth of that. The ink is costed
 * against both and takes the better, so a stroke a reader simplifies the way
 * every hand simplifies it is still the stroke.
 *
 * @param drawn - The ink, described in the square.
 * @param fine - The stroke at the measured tolerance.
 * @param coarse - The same stroke described coarsely, or nothing.
 * @returns The lower cost.
 */
export function leastCost(
  drawn: Stroke,
  fine: Stroke,
  coarse: Stroke | undefined,
): number {
  const cost = strokeCost(drawn, fine);

  return coarse === undefined
    ? cost
    : Math.min(cost, strokeCost(drawn, coarse));
}

/**
 * The index of the cheapest cost.
 *
 * @param costs - At least one cost.
 * @returns Where the cheapest sits, the first of them where they tie.
 */
export function cheapest(costs: readonly number[]): number {
  let at = 0;

  costs.forEach((cost, index) => {
    if (cost < (costs[at] as number)) {
      at = index;
    }
  });

  return at;
}

/**
 * The stroke moved so that its middle sits where another's does.
 *
 * `Stroke.column` and `Stroke.row` are the middle as a fraction of the place
 * grid, so the move is their difference scaled back to the square. What
 * `strokeCost` then reads is the shape and the length with the place taken
 * out, which is the question asked before saying a stroke was right and in
 * the wrong place.
 *
 * @param points - The ink, in the square.
 * @param from - The ink described, for where its middle is.
 * @param onto - The stroke whose middle the ink is moved to.
 * @returns The ink moved, in the square.
 */
export function movedOnto(
  points: StrokePoints,
  from: Stroke,
  onto: Stroke,
): StrokePoints {
  const cells = placeSteps - 1;
  const acrossBy = (onto.column - from.column) / cells;
  const downBy = (onto.row - from.row) / cells;

  return points.map(([x, y]): StrokePoint => [x + acrossBy, y + downBy]);
}
